import type { TransactionCategoryFacet } from '@/lib/api';
import type { Transaction } from '@/lib/transactions/types';
import { countedAmount, isFlowNeutral } from '@/lib/transactions/refunds';
import type { CategoryIndex } from '../screen/categoryIndex';
import { getCategoryMeta } from '../screen/categoryIndex';

export interface CategoryRow {
  id: string;
  label: string;
  iconName: string;
  color: string;
  amount: number;
  pct: number;
  count: number;
}

/**
 * Aggregate per-leaf category facets up to parent categories so the
 * full-list drawer mirrors the keys used by the top-5 breakdown card
 * (which is keyed by parentCategoryId via `categoryFilterIdByCategoryId`).
 */
export function buildParentCategoryRows(
  facets: readonly TransactionCategoryFacet[] | undefined,
  categoryIndex: CategoryIndex,
): CategoryRow[] {
  if (!facets || facets.length === 0) return [];
  const byParent = new Map<string, { amount: number; count: number }>();
  let total = 0;
  for (const f of facets) {
    const v = Math.abs(f.amount);
    if (v === 0) continue;
    const parentId = f.parentCategoryId ?? f.id;
    const cur = byParent.get(parentId) ?? { amount: 0, count: 0 };
    cur.amount += v;
    cur.count += f.count;
    byParent.set(parentId, cur);
    total += v;
  }
  const rows: CategoryRow[] = [];
  for (const [id, agg] of byParent) {
    const meta = getCategoryMeta(categoryIndex, id);
    rows.push({
      id,
      label: meta.label,
      iconName: meta.iconName,
      color: meta.color,
      amount: agg.amount,
      pct: total > 0 ? (agg.amount / total) * 100 : 0,
      count: agg.count,
    });
  }
  rows.sort((a, b) => b.amount - a.amount);
  return rows;
}

/**
 * Sub-category rows aggregated from the *transactions* scoped to one parent.
 *
 * Server `categoryFacets` are parent-level (one entry per parent), so they
 * cannot back the sub-category donut — using them yields a single 100% slice.
 * Transactions carry the leaf `categoryId`, so we aggregate from those.
 */
export function buildSubcategoryRowsFromTxs(
  txs: readonly Transaction[],
  parentCategoryId: string,
  categoryIndex: CategoryIndex,
): CategoryRow[] {
  const byLeaf = new Map<string, { amount: number; count: number }>();
  let total = 0;
  for (const t of txs) {
    if (isFlowNeutral(t)) continue;
    const parent = t.parentCategoryId ?? t.categoryId;
    if (parent !== parentCategoryId) continue;
    const leaf = t.categoryId || parent;
    // Counted remainder, not the face value — a partially refunded −120 with 50
    // back contributes 70, and a fully refunded row contributes nothing.
    const v = Math.abs(countedAmount(t));
    if (v === 0) continue;
    const cur = byLeaf.get(leaf) ?? { amount: 0, count: 0 };
    cur.amount += v;
    cur.count += 1;
    byLeaf.set(leaf, cur);
    total += v;
  }
  const rows: CategoryRow[] = [];
  for (const [id, agg] of byLeaf) {
    const meta = getCategoryMeta(categoryIndex, id);
    rows.push({
      id,
      // 3rd-level leaves carry their breadcrumb ("Restaurantes › Delivery") so a
      // grandchild is never ambiguous next to its uncle rows.
      label: meta.pathLabel ?? meta.label,
      iconName: meta.iconName,
      color: meta.color,
      amount: agg.amount,
      pct: total > 0 ? (agg.amount / total) * 100 : 0,
      count: agg.count,
    });
  }
  rows.sort((a, b) => b.amount - a.amount);
  return rows;
}
